import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  lazy,
  Suspense,
} from "react";
import { strings } from "../resources/strings";
import { routes } from "../router/routes";
import { Link, useLocation } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { signOut } from "aws-amplify/auth";
import { useAgencyPermissionValidation } from "../context/AgencyPermissionValidationContext";
import { useChatEnabled } from "./Chat/hooks/useChatEnabled";
import ChatNavBadge from "./Chat/ChatNavBadge";
import { useResourcesParticipantWebappFlag } from "../utils/launchdarkly/featureFlags";

// Lazy: the watcher pulls in the chat Apollo client (appsync auth/subscription
// links), which must not ride the main bundle for participants without chat.
// It is mounted exactly once — the desktop header and mobile drawer badges are
// both always in the DOM (CSS-hidden, not unmounted), so fetching inside the
// badge would double every chat subscription.
const ChatUnreadWatcher = lazy(() => import("./Chat/ChatUnreadWatcher"));

// TypeScript interfaces
interface Route {
  path: string;
  name: string;
}

interface AuthenticatorContext {
  authStatus: string;
  user?: any; // AWS Amplify user object structure varies
}

// Constants
const NAVBAR_CONFIG = {
  height: "h-20",
  colors: {
    background: "bg-primary",
    text: "text-white",
    accent: "text-navbar-accent",
    border: "border-navbar-accent",
  },
  spacing: {
    buttonHeight: "h-11",
    buttonPadding: "py-2 px-2", // 8px top/bottom, 8px left/right
    borderRadius: "rounded-lg",
    navGap: "gap-3", // 12px between items: all of them fit from 1280px, in Spanish too
  },
  typography: {
    size: "text-xl",
    weight: {
      normal: "font-normal",
      bold: "font-bold",
    },
    lineHeight: "leading-none",
    tracking: "tracking-normal",
  },
  transitions: "transition-colors duration-200",
  shadow: "shadow-navbar",
} as const;

/** Stable id for the drawer panel — `aria-controls` target, and the anchor the
 *  a11y tab-order probe uses to classify a stop as in-drawer. It must NOT be a
 *  state-gated attribute like `role="dialog"`: keying the probe on one of those
 *  would make "zero drawer stops while closed" pass by reclassification rather
 *  than because the drawer is actually inert. */
const DRAWER_PANEL_ID = "mobile-drawer-panel";

/** Tailwind's `xl`: the row of links is shown from here, the drawer below it. */
const DESKTOP_NAV_QUERY = "(min-width: 1280px)";

const TOGGLE_UNREAD_ID = "drawer-toggle-unread";

const MOBILE_DRAWER_CONFIG = {
  width: "w-64",
  background: "bg-primary",
  divider: "divide-y-2 divide-white/20",
} as const;

const SIGN_OUT_BUTTON_CONFIG = {
  width: "w-32",
  height: "h-12",
  borderRadius: "rounded-xl",
  border: "border border-primary",
  background: "bg-navbar-secondary",
  textColor: "text-primary",
  fontSize: "text-sm",
  fontWeight: "font-medium",
  shadow: "shadow-sign-out",
  hoverShadow: "hover:shadow-sign-out-hover",
} as const;

/**
 * Whether a nav item is the current page. Exact, except that a resource's
 * detail page still counts as Resources, so the item keeps aria-current.
 */
export const isActiveNavItem = (pathname: string, itemPath: string): boolean =>
  pathname === itemPath ||
  (itemPath === routes.resources.path &&
    pathname.startsWith(`${routes.resources.path}/`));

const NavBar: React.FC = () => {
  const [sideMenuExpanded, setSideMenuExpanded] = useState<boolean>(false);
  const currentLocation = useLocation();

  const { canAccessParticipantWebapp, loadingAgency } =
    useAgencyPermissionValidation();

  const { authStatus } = useAuthenticator((context: AuthenticatorContext) => [
    context.authStatus,
  ]);
  const isAuthenticated = useCallback(
    (authStatus: string): boolean => authStatus === "authenticated",
    [],
  );

  // Chat nav item appears only when the feature is enabled for this
  // participant (LD flag + provisioned channel + configured endpoint).
  const { enabled: chatEnabled, chatUserId } = useChatEnabled();
  const [chatUnreadCount, setChatUnreadCount] = useState<number>(0);
  // The count outlives the watcher when the Chat flag turns off; never show it then.
  const toggleUnread = chatEnabled ? chatUnreadCount : 0;
  const resourcesEnabled = useResourcesParticipantWebappFlag();

  // Memoized route arrays to prevent recreation on every render
  const navHeaderRoutes = useMemo<Route[]>(
    () => [
      routes.dashboard,
      routes.appointments,
      routes.documents,
      routes.termsOfUse,
      routes.officerProfile,
      ...(chatEnabled ? [routes.chat] : []),
      ...(resourcesEnabled ? [routes.resources] : []),
    ],
    [chatEnabled, resourcesEnabled],
  );

  const sideMenuRoutes = useMemo<Route[]>(
    () => [
      routes.dashboard,
      routes.appointments,
      routes.documents,
      routes.termsOfUse,
      routes.officerProfile,
      ...(chatEnabled ? [routes.chat] : []),
      ...(resourcesEnabled ? [routes.resources] : []),
    ],
    [chatEnabled, resourcesEnabled],
  );

  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Distinguishes "closed because it was never opened" from "closed after being
  // open". Without it the focus-return effect fires on mount and steals focus.
  const hasBeenOpen = useRef(false);
  // Whether the CURRENT close was an explicit dismiss — the toggle, the close
  // button, the backdrop or Escape. Only those get focus back on the toggle.
  // Closing because the participant navigated, or because they tabbed forward
  // out of the panel, must not drag focus backwards (2.4.3).
  const dismissedDeliberately = useRef(false);

  // Memoized event handlers for performance
  const toggleSideMenu = useCallback(() => {
    setSideMenuExpanded((prev) => {
      if (prev) {
        dismissedDeliberately.current = true;
      }
      return !prev;
    });
  }, []);

  // Move focus into the drawer on open, and back to the toggle only when the
  // participant dismissed it themselves.
  useEffect(() => {
    if (sideMenuExpanded) {
      hasBeenOpen.current = true;
      closeRef.current?.focus();
      return;
    }
    if (hasBeenOpen.current && dismissedDeliberately.current) {
      toggleRef.current?.focus();
    }
    dismissedDeliberately.current = false;
  }, [sideMenuExpanded]);

  // Following a drawer link was not a close path: the Navbar does not unmount
  // across routes, so the menu stayed open over the new page. Deliberately no
  // focus return here — the participant asked to go somewhere, and dropping
  // them back on the hamburger would fight that.
  useEffect(() => {
    setSideMenuExpanded(false);
  }, [currentLocation.pathname]);

  // Widening the window past xl hides the toggle; an open drawer would stay over
  // the page with nowhere for focus to return. Close it without a focus return.
  // Guarded: jsdom has no matchMedia.
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const desktop = window.matchMedia(DESKTOP_NAV_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setSideMenuExpanded(false);
    };
    desktop.addEventListener("change", onChange);
    return () => desktop.removeEventListener("change", onChange);
  }, []);

  // Tabbing past the last item closes the menu instead of stranding focus
  // behind a dimmed overlay the participant can no longer see past. relatedTarget
  // is the element receiving focus; null means focus left the document entirely.
  const handlePanelBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    setSideMenuExpanded(false);
  }, []);

  // Escape closes. Registered only while open, so it cannot swallow Escape from
  // any other surface (the wizard and onboarding dialogs own their own).
  useEffect(() => {
    if (!sideMenuExpanded) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        dismissedDeliberately.current = true;
        setSideMenuExpanded(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [sideMenuExpanded]);

  const handleSignOut = useCallback(async () => {
    try {
      // { global: true } ends all Cognito session (clears its
      // tokens from local storage). We only allow participants to
      // log into a single device at a time
      await signOut({ global: true });
    } catch (error) {
      // Reload below still logs the participant out of this browser even if
      // the network call to Cognito failed.
      console.error("Sign out request failed:", error);
    } finally {
      /**
       * This is a workaround to prevent an issue we are experiencing with the AWS Amplify
       * Authenticator component.
       *
       * The issue is reproduced via:
       * - sign in, refresh, and then sign out
       * - the user is redirected to the login page, but the form input fields are
       *   not properly displayed. (Shows "Username" input instead of "Phone Number")
       *
       * After reloading, the form input fields are displayed correctly.
       *
       * Awaiting signOut() above ensures the tokens are actually cleared from
       * local storage before the reload runs.
       */
      // eslint-disable-next-line no-restricted-globals
      location.reload();
    }
  }, []);

  // Function to render the top header navigation links.
  // Each item is a SINGLE interactive element (the <Link>/<a>) — the button
  // styling is applied directly to the anchor (EHA-913: previously an
  // <a><button> which is invalid nested-interactive HTML). The active-state
  // underline lives on a NON-interactive wrapper <div> (not the rounded anchor)
  // so it stays a straight full-width line, visually identical to before.
  const configureNavigationHeader = useCallback(() => {
    return navHeaderRoutes.map((item: Route, index: number) => {
      const isActive = isActiveNavItem(currentLocation.pathname, item.path);
      return (
        <div
          key={index}
          className={`flex shrink-0 ${isActive ? "border-b-3 border-navbar-accent" : ""}`}
        >
          <Link
            to={item.path}
            aria-current={isActive ? "page" : undefined}
            className={`
              ${NAVBAR_CONFIG.spacing.buttonHeight}
              ${NAVBAR_CONFIG.spacing.borderRadius}
              ${NAVBAR_CONFIG.spacing.buttonPadding}
              inline-flex items-center justify-center
              no-underline
              ${NAVBAR_CONFIG.colors.text}
                             hover:bg-white/10
               focus:outline-none
               focus-visible:bg-white/10
               focus-visible:ring-2
               focus-visible:ring-white/50
              normal-case
              ${NAVBAR_CONFIG.typography.size}
              ${NAVBAR_CONFIG.typography.lineHeight}
              ${NAVBAR_CONFIG.typography.tracking}
              ${NAVBAR_CONFIG.transitions}
              ${isActive ? NAVBAR_CONFIG.typography.weight.bold : NAVBAR_CONFIG.typography.weight.normal}
            `}
          >
            {item.name}
            {item.path === routes.chat.path && (
              <ChatNavBadge count={chatUnreadCount} />
            )}
          </Link>
        </div>
      );
    });
  }, [navHeaderRoutes, currentLocation.pathname, chatUnreadCount]);

  // Function to render the content within the expandable side-menu
  const buildSideMenuContent = useCallback(() => {
    return sideMenuRoutes.map((item: Route, index: number) => {
      const isActive = isActiveNavItem(currentLocation.pathname, item.path);
      return (
        <Link
          to={item.path}
          key={index}
          aria-current={isActive ? "page" : undefined}
          // Belt and braces with the `inert` on the drawer wrapper. `inert` is
          // the better tool (it also removes the subtree from the accessibility
          // tree) but its support floor is newer than this app's browserslist
          // target of iOS Safari 15.6, and if it no-ops the seven invisible
          // focus stops silently return. tabIndex is supported everywhere.
          tabIndex={sideMenuExpanded ? undefined : -1}
          // Ring matches the drawer's Sign Out button. Batch A left these
          // unstyled because they were unreachable; this batch makes them
          // reachable, so without a ring it would CREATE a 2.4.7/1.4.11
          // failure (the UA default ring measures 2.05:1 here).
          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded"
        >
          <div
            className={`${NAVBAR_CONFIG.colors.accent} font-semibold hover:bg-white/10 w-full py-4 px-4 ${isActive ? "bg-white/5" : ""}`}
          >
            {item.name}
            {item.path === routes.chat.path && (
              <ChatNavBadge count={chatUnreadCount} />
            )}
          </div>
        </Link>
      );
    });
  }, [
    sideMenuRoutes,
    currentLocation.pathname,
    chatUnreadCount,
    sideMenuExpanded,
  ]);

  if (
    !isAuthenticated(authStatus) ||
    !canAccessParticipantWebapp ||
    loadingAgency
  ) {
    return null;
  }

  return (
    <>
      {/* Single unread-count pipeline feeding both badge render sites */}
      {chatEnabled && (
        <Suspense fallback={null}>
          <ChatUnreadWatcher
            chatUserId={chatUserId}
            onUnreadChange={setChatUnreadCount}
          />
        </Suspense>
      )}
      <nav
        className={`navbar ${NAVBAR_CONFIG.height} rounded-b-sm ${NAVBAR_CONFIG.colors.background} ${NAVBAR_CONFIG.shadow} px-0 py-0 fixed top-0 left-0 right-0 z-50`}
      >
        <div className="drawer">
          <div className="drawer-content flex flex-col">
            {/* Navbar Drawer Button - visible below xl (1280px) */}
            <div className="w-full flex-1 navbar px-0 mx-0">
              <div className="flex-none xl:hidden">
                {/* Drawer Open Button (below xl: phones, tablets and narrow laptops) */}
                <button
                  type="button"
                  ref={toggleRef}
                  aria-label={strings.navbar.openSidebar}
                  aria-expanded={sideMenuExpanded}
                  aria-controls={DRAWER_PANEL_ID}
                  // Below xl the header links are hidden, so the unread count
                  // rides on the toggle. A description, not the name: the name
                  // stays "open sidebar".
                  aria-describedby={
                    toggleUnread > 0 ? TOGGLE_UNREAD_ID : undefined
                  }
                  // outline, NOT ring: daisyUI's .btn sets its own focus-visible
                  // box-shadow (white at 7.5%, ~0.3px — measured at 1.57:1), which
                  // beats Tailwind's ring utility. Batch A hit the identical trap on
                  // Tile.tsx, where `outline-none` + a ring that never painted left
                  // the control worse off than untouched.
                  className={`relative btn btn-square btn-ghost ${NAVBAR_CONFIG.colors.text} pr-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white`}
                  onClick={toggleSideMenu}
                >
                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    className="inline-block w-6 h-6 stroke-current"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                  {toggleUnread > 0 && (
                    <span
                      id={TOGGLE_UNREAD_ID}
                      data-testid="drawer-toggle-unread"
                      className="absolute -top-1 right-0 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-secondary text-primary text-xs font-bold leading-none"
                    >
                      <span aria-hidden="true">
                        {toggleUnread > 9 ? "9+" : toggleUnread}
                      </span>
                      <span className="sr-only">
                        {`${toggleUnread} ${strings.chat.unreadMessages}`}
                      </span>
                    </span>
                  )}
                </button>
              </div>

              {/* RePath Logo */}
              <div className="px-0 ml-2 mr-3 xl:ml-6 xl:mr-8 z-0">
                <img
                  src="/repath-logo-vector.svg"
                  className="h-10"
                  alt="RePath logo"
                />
              </div>

              <div className="flex-1 w-full flex items-center min-w-0">
                {/*
                  Page navigation buttons, shown from xl (1280px). Below that the
                  drawer is used: six or seven items in Spanish only fit from
                  about 1280px, and a row that scrolls sideways hid
                  the last ones behind a scrollbar nobody noticed. The row is still
                  horizontally scrollable (overflow-x-auto + min-w-0) as a safety
                  net, e.g. for 200% text, so it never pushes Sign Out off-screen.
                  `overflow-x-auto` also clips overflow-y, and the container is
                  only as tall/wide as the items — so `p-1` gives the hover box
                  + focus-visible ring room on all sides. Without it the first
                  item's ring is cut on the left and the active item's ring
                  (its wrapper is 3px taller from the underline) is cut on top.
                */}
                <div
                  data-testid="navbar-scroll-container"
                  className={`hidden xl:flex items-center flex-1 min-w-0 overflow-x-auto p-1 ${NAVBAR_CONFIG.spacing.navGap}`}
                >
                  {/* Navbar menu content rendered here, using the defined routes object */}
                  {configureNavigationHeader()}
                </div>

                {/* Sign Out button - always pinned to the top right corner */}
                <div
                  data-testid="navbar-signout-wrapper"
                  className="pr-2 pl-1 xl:pr-10 xl:pl-3 shrink-0 ml-auto"
                >
                  <button
                    onClick={handleSignOut}
                    className={`
                       flex items-center justify-center 
                       ${SIGN_OUT_BUTTON_CONFIG.width} ${SIGN_OUT_BUTTON_CONFIG.height}
                       ${SIGN_OUT_BUTTON_CONFIG.borderRadius} ${SIGN_OUT_BUTTON_CONFIG.border} 
                       p-2 gap-1 ${SIGN_OUT_BUTTON_CONFIG.background} ${SIGN_OUT_BUTTON_CONFIG.textColor}
                       ${SIGN_OUT_BUTTON_CONFIG.shadow} transition-all duration-200 ease-in-out
                       ${SIGN_OUT_BUTTON_CONFIG.hoverShadow} focus:outline-none focus:ring-2 
                       focus:ring-offset-2 focus:ring-primary normal-case 
                       ${SIGN_OUT_BUTTON_CONFIG.fontSize} ${SIGN_OUT_BUTTON_CONFIG.fontWeight}
                     `}
                    aria-label={strings.navbar.signOutAria}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16,17 21,12 16,7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    {strings.signOut}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 
            Side Navigation Menu 
            - Button to open the side menu is visible below xl (1280px)
            - If the menu is not open, the width is set to zero to prevent the content overlay from receiving clicks
          */}
          {/*
            Backdrop. `inert` while closed is what keeps the drawer's links out
            of the tab order: the panel is only translated off-screen and the
            backdrop only faded, and NEITHER a zero opacity nor an off-screen
            transform removes an element from the tab order. Before this, every
            route carried seven invisible focus stops.

            This is a DISCLOSURE, not a modal dialog. The previous markup put
            role="dialog" aria-modal="true" on this backdrop permanently, which
            told screen readers the whole app was inside an open modal. Honest
            modality is not available to this component: App.tsx renders the
            routed page as a SIBLING of <Navbar>, so the drawer cannot inert or
            hide it, and aria-modal without that backing is a promise the DOM
            does not keep. The toggle's aria-expanded/aria-controls is the
            correct pattern here, and focus-out closes the menu so a keyboard
            user is never tabbing behind a dimmed overlay.
          */}
          {/* The scrim is click-to-dismiss for pointer users only. It is never
              focusable, so a key handler here would be unreachable; keyboard
              users close the drawer with the toggle or by tabbing out. */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            data-testid="mobile-drawer-backdrop"
            inert={!sideMenuExpanded}
            className={`
              fixed inset-0 bg-black bg-opacity-50 transition-opacity 
              ${sideMenuExpanded ? "opacity-100" : "opacity-0"}
              ${sideMenuExpanded ? "w-full" : "w-0"}
              ${sideMenuExpanded ? "z-50" : "z-0"}
            `}
            onClick={toggleSideMenu}
          >
            {/* onBlur is the focus-out close described above; the click handler
                only stops the scrim's dismiss from firing for clicks inside the
                panel. Neither is an action a key handler could add. */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
            <div
              id={DRAWER_PANEL_ID}
              ref={panelRef}
              // role="group" is what makes the name legal: aria-labelledby on a
              // roleless div is naming a `generic`, which ARIA prohibits, so the
              // sr-only "Navigation menu" heading named nothing and axe reports
              // aria-prohibited-attr. group is the least invasive role that
              // accepts a name — region would add a second landmark to a page
              // whose <nav> already is one.
              role="group"
              aria-labelledby="mobile-menu-title"
              onBlur={handlePanelBlur}
              className={`
                fixed inset-y-0 left-0 ${MOBILE_DRAWER_CONFIG.width} ${MOBILE_DRAWER_CONFIG.background} rounded-r-lg shadow-lg
                transform transition-transform 
                ${sideMenuExpanded ? "translate-x-0" : "-translate-x-full"}
              `}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`grid grid-cols-1 ${MOBILE_DRAWER_CONFIG.divider}`}
              >
                <h2 id="mobile-menu-title" className="sr-only">
                  {strings.navbar.navigationMenu}
                </h2>
                {/*
                  Visible close control. Once the page behind is inert, Escape
                  is otherwise the only keyboard exit and the toggle that opened
                  the drawer sits underneath the panel — so there would be no
                  on-screen way out. First focusable child, so opening lands
                  here.
                */}
                <div className="flex justify-end px-2 pt-2">
                  <button
                    type="button"
                    ref={closeRef}
                    onClick={toggleSideMenu}
                    tabIndex={sideMenuExpanded ? undefined : -1}
                    aria-label={strings.navbar.closeSidebar}
                    className={`${NAVBAR_CONFIG.colors.accent} rounded p-2 text-xl leading-none hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50`}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                {buildSideMenuContent()}
                {/* Sign Out option in mobile menu */}
                <div
                  className={`${NAVBAR_CONFIG.colors.accent} font-semibold hover:bg-white/10 w-full py-4 px-4`}
                >
                  <button
                    onClick={handleSignOut}
                    tabIndex={sideMenuExpanded ? undefined : -1}
                    className="w-full text-left focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-1"
                  >
                    {strings.signOut}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default NavBar;
